import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Country, State, City } from "country-state-city";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "libphonenumber-js";
import { ColorRing } from "react-loader-spinner";
import { db, analytics, Timestamp, storage } from "../firebase-config";
import { addDoc, collection, doc, getDoc, setDoc } from "firebase/firestore";
import { logEvent } from "firebase/analytics";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import useDateUtils from "../utils/fromStringToTimestamp";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion"; // NEW: For Animations & Parallax

// Allowed file types and Size.
const fileType = [
  "text/plain",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/x-abiword",
  "application/octet-stream",
];
const fileSizeLimit = 5000000; // 5MB
const jobDocRef = doc(db, `job`, "5tdpJAv6JNwmawkDKPSH");

// Common Input styling for modularity
const inputClasses = "w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#A47713] focus:border-[#A47713] transition-all duration-300 bg-white/70 backdrop-blur-sm hover:shadow-md outline-none text-slate-700 leading-relaxed";
const labelClasses = "block text-slate-600 leading-relaxed text-sm font-semibold mb-2 ml-1";
const cardClasses = "bg-white rounded-2xl shadow-xl border border-slate-200/50 p-6 sm:p-8 mb-8 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl";

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const { formatToFrenchLongDate } = useDateUtils();
  const { jobID } = useParams();

  // Parallax effects
  const { scrollYProgress } = useScroll();
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  // --- EXISTING STATE LOGIC ---
  const [jobIdInput, setJobIdInput] = useState("");
  const [tenantID, setTenantID] = useState("");
  const [jobUuid, setJobUuid] = useState("");
  const [jobLanguage, setJobLanguage] = useState("");
  const [jobDetails, setJobDetails] = useState({});
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [selectedCountryisoCode, setSelectedCountryisoCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [educations, setEducations] = useState([
    { id: uuidv4(), Institution: "", Degree: "", FieldOfStudy: "", GraduationYear: "" },
  ]);
  const [references, setReferences] = useState([
    { RefereeName: "", Relation: "", ContactInformation: "" },
  ]);
  const [certificates, setCertificates] = useState([
    { CertificationName: "", IssuingOrganization: "", DateObtained: "", ExpiryDate: "" },
  ]);
  const [certificatesDocument, setCertificatesDocument] = useState(null);
  const [diplomas, setDiplomas] = useState(null);
  const [languages, setLanguages] = useState([{ Language: "", Proficiency: "" }]);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [educationErrors, setEducationErrors] = useState([
    { Institution: false, Degree: false, FieldOfStudy: false, GraduationYear: false },
  ]);
  const [fullName, setFullName] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [skills, setSkills] = useState("");
  const [sex, setSex] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [resumeError, setResumeError] = useState("");
  const [coverLetterError, setCoverLetterError] = useState("");
  const [certificateError, setCertificateError] = useState("");
  const [diplomaError, setDiplomaError] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [certificateName, setCertificateName] = useState("");
  const [coverLetterName, setCoverLetterName] = useState("");
  const [diplomaName, setDiplomaName] = useState("");
  const [resumeUploadError, setResumeUploadError] = useState(false);
  const [jobNotFound, setJobNotFound] = useState(false);
  const [canApply, setCanApply] = useState(true);

  const { t } = useTranslation("ApplicationsPage");

  const [tempUserId, setTempUserId] = useState(() => {
    let uid = localStorage.getItem("tempUserId");
    if (!uid) {
      uid = uuidv4();
      localStorage.setItem("tempUserId", uid);
    }
    return uid;
  });

  // --- EXISTING USE EFFECTS ---
  useEffect(() => {
    document.title = "Job Applications Page - ProRecruit";
    return () => { document.title = "ProRecruit"; };
  }, []);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (jobID && jobNotFound) {
        setJobNotFound(false);
        return;
      }
      if (!jobID) {
        navigate("/job_listings");
        return;
      }
      setLoading(true);
      try {
        const jobObjectDoc = await getDoc(jobDocRef);
        if (!jobObjectDoc.exists()) {
          console.log("Job mapping document not found");
          setJobNotFound(true);
          return;
        }
        const jobObjectData = jobObjectDoc.data();
        const jobReference = jobObjectData ? jobObjectData[jobID] : null;
        if (!jobReference) {
          console.log("Job reference not found in job mapping doc");
          setJobNotFound(true);
          return;
        }
        const jobDoc = await getDoc(doc(db, jobReference));

        if (!jobDoc.exists()) {
          console.log("Job not found");
          setJobNotFound(true);
          return;
        }

        const data = jobDoc.data();
        setTenantID(data.tenantId);
        setJobUuid(data.uuid);
        setJobLanguage(data.language ? data.language : "fr");

        let formattedDateLimite = data.DateLimiteDeCandidature;

        if (data.DateLimiteDeCandidature) {
          let timeInMs = null;
          if (data.DateLimiteDeCandidature instanceof Timestamp) {
            timeInMs = data.DateLimiteDeCandidature.toMillis();
          } else if (data.DateLimiteDeCandidature.seconds) {
            timeInMs = data.DateLimiteDeCandidature.seconds * 1000;
          } else if (data.DateLimiteDeCandidature instanceof Date) {
            timeInMs = data.DateLimiteDeCandidature.getTime();
          } else if (typeof data.DateLimiteDeCandidature === 'object') {
            formattedDateLimite = ""; // prevent React child objects crash
          }

          if (timeInMs) {
            const limitTime = timeInMs + 86400000 - 1;
            const nowTime = Timestamp.now().toMillis();
            if (limitTime < nowTime) {
              console.log(`Date limit: ${limitTime} < ${nowTime}`);
              setCanApply(false);
            }
            formattedDateLimite = new Date(timeInMs).toISOString().split('T')[0];
          }
        }

        setJobDetails({
          Titre: data.Titre,
          DateLimiteDeCandidature: formattedDateLimite,
          Description: data.Description,
          companyName: data.Entreprise,
          location: data.Lieu,
          competences: data.CompetencesRequises,
          companyDescription: data.EntrepriseDescription,
          qualifications: data.Qualifications,
          responsibilities: data.Responsibilities,
          type: data.TypeDePoste,
        });
      } catch (error) {
        console.error("Error fetching job details:", error);
        setJobNotFound(true);
        if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
          Sentry.captureException(error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchJobDetails();
  }, [jobID, tenantID]);

  // --- EXISTING HANDLERS ---
  const handleJobIdSubmit = (event) => {
    event.preventDefault();
    if (jobIdInput) navigate(`/jobs/${jobIdInput}`);
  };

  const handleFileUpload = async (file, folder, fileNameExtraDetails) => {
    if (!file) return "";
    try {
      const metadata = { contentType: file.type };

      if (file.type === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdfData = new Uint8Array(arrayBuffer);
          if (pdfData.length > 4 && String.fromCharCode(pdfData[0], pdfData[1], pdfData[2], pdfData[3]) !== '%PDF') {
            setResumeUploadError(true);
            alert(t("invalidPDFError"));
            await addDoc(collection(db, "uploading-errors"), {
              tenantID,
              jobUuid,
              resumeName,
              coverLetterName,
              certificateName,
              diplomaName,
              candidateinfo: {
                fullName,
                email,
                phoneNumber,
                selectedCountryisoCode,
                selectedState,
                selectedCity,
                educations,
                skills,
                languages,
                diplomas,
              },
            error: "Invalid PDF Header",
            timestamp: Timestamp.now(),
            });
            return "";
          }
        } catch (preliminaryError) {
          setResumeUploadError(true);
          alert(t("invalidPDFError"));
          await addDoc(collection(db, "uploading-errors"), {
            tenantID,
            jobUuid,
            resumeName,
            coverLetterName,
            certificateName,
            diplomaName,
            candidateinfo: {
              fullName,
              email,
              phoneNumber,
              selectedCountryisoCode,
              selectedState,
              selectedCity,
              educations,
              skills,
              languages,
              diplomas,
            },
            error: "Invalid PDF Header",
            timestamp: Timestamp.now(),
          });
          return "";
        }
      }
      const uniqueFilename = `${fileNameExtraDetails}_${uuidv4()}_${file.name.replace(/ /g, "")}`;
      const storageRef = ref(storage, `uploads/${folder}/${tempUserId}/${uniqueFilename}`);
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      return new Promise((resolve, reject) => {
        uploadTask.on("state_changed",
          (snapshot) => {
            console.log(snapshot);
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
            switch (snapshot.state) {
              case 'paused':
                console.log('Upload is paused');
                break;
              case 'running':
                console.log('Upload is running');
                break;
              default:
                break;
            }
          },
          (error) => { reject(error); },
          () => {
            console.log(uploadTask.snapshot.metadata.fullPath);
            console.log(storageRef)
            resolve({ uniqueFilename, storageRef: uploadTask.snapshot.metadata.fullPath });
          }
        );
      });
    } catch (error) {
      setResumeUploadError(true);
      await addDoc(collection(db, "uploading-errors"), {
        tenantID,
        jobUuid,
        resumeName,
        coverLetterName,
        certificateName,
        diplomaName,
        candidateinfo: {
          fullName,
          email,
          phoneNumber,
          selectedCountryisoCode,
          selectedState,
          selectedCity,
          educations,
          skills,
          languages,
          diplomas,
        },
        error: error.message,
        timestamp: Timestamp.now(),
      });
      if (error.message.includes("PDF")) {
        alert(t("invalidPDFError"));
      } else {
        alert(t("uploadError"));
      }
      return "";
    }
  };

  const handleCountryChange = (event) => {
    const isoCode = event.target.value;
    setSelectedCountryisoCode(isoCode);
    setSelectedCountry(Country.getCountryByCode(isoCode));
    setSelectedState("");
    setSelectedCity("");
  };

  const handlePhoneNumberChange = (e) => {
    const inputValue = e.target.value;
    setPhoneNumber(inputValue);
    if (!/^[0-9+ -]*$/.test(inputValue)) return;
    setPhoneNumberError("");
  };

  const handlePhoneNumberBlur = () => {
    if (!phoneNumber) return;
    const number = parsePhoneNumberFromString(phoneNumber, selectedCountryisoCode || "TG");
    if (!number || !isPossiblePhoneNumber(`${number.number}`) || !isValidPhoneNumber(`${number.number}`)) {
      setPhoneNumberError(t("invalidPhoneNumber"));
    }
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const file = files[0];

    if (!file) {
      if (name === "resume") { setResumeError(""); setResume(null); }
      if (name === "coverLetter") { setCoverLetterError(""); setCoverLetter(null); }
      if (name === "certificatesDocument") { setCertificateError(""); setCertificatesDocument(null); }
      if (name === "diplomas") { setDiplomaError(""); setDiplomas(null); }
      return;
    }

    let error = "";
    if (!fileType.includes(file.type)) error = t("invalidFileType");
    else if (file.size > fileSizeLimit) error = t("fileTooLarge", { maxSize: fileSizeLimit / 1000 });

    if (error) {
      if (name === "resume") setResumeError(error);
      if (name === "coverLetter") setCoverLetterError(error);
      if (name === "certificatesDocument") setCertificateError(error);
      if (name === "diplomas") setDiplomaError(error);
      alert(error);
      return;
    }

    let fn = `${`${fullName.replace(/\s/g, "")}`}_${new Date().toUTCString()}`;
    fn = fn.replaceAll(" ", "_").replaceAll(",", "").replaceAll(":", "_");

    switch (name) {
      case "resume": setResumeName(`Resume_${fn}`); setResume(file); setResumeError(""); break;
      case "coverLetter": setCoverLetterName(`CoverLetter_${fn}`); setCoverLetter(file); setCoverLetterError(""); break;
      case "certificatesDocument": setCertificateName(`Certificate_${fn}`); setCertificatesDocument(file); setCertificateError(""); break;
      case "diplomas": setDiplomaName(`Diploma_${fn}`); setDiplomas(file); setDiplomaError(""); break;
      default: break;
    }
  };

  const addOrRemoveItem = (array, setArray, item = {}, index) => {
    if (item && Object.keys(item).length > 0) {
      setArray([...array, { ...item, id: uuidv4() }]);
    } else {
      const newArray = [...array];
      newArray.splice(index, 1);
      setArray(newArray);
    }
  };

  const handleArrayInputChange = (array, setArray, index, event) => {
    const newArray = [...array];
    newArray[index] = { ...newArray[index], [event.target.name]: event.target.value };
    setArray(newArray);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDisableSubmitButton(true);
    setLoading(true);
    setSubmissionAttempted(true);
    setResumeUploadError(false);
    setResumeError(""); setCoverLetterError(""); setCertificateError(""); setDiplomaError(""); setPhoneNumberError("");

    const newEducationErrors = educations.map((edu) => ({
      Institution: !edu.Institution, Degree: !edu.Degree, FieldOfStudy: !edu.FieldOfStudy, GraduationYear: !edu.GraduationYear,
    }));
    setEducationErrors(newEducationErrors);

    const isValidEducation = educations.some(edu => edu.Institution && edu.Degree && edu.FieldOfStudy && edu.GraduationYear);
    const isValid = !newEducationErrors.some((err) => Object.values(err).includes(true));

    if (!isValid || !isValidEducation) {
      alert(!isValidEducation ? t("fillRequiredEducation") : t("fixErrorsBeforeSubmit"));
      setDisableSubmitButton(false); setLoading(false); return;
    }

    if (!resume) {
      setResumeError(t("resumeRequired")); alert(t("resumeRequired"));
      setDisableSubmitButton(false); setLoading(false); return;
    }

    const number = parsePhoneNumberFromString(phoneNumber, selectedCountryisoCode);
    if (!phoneNumber || !number || !isValidPhoneNumber(`${number.number}`)) {
      setPhoneNumberError(!phoneNumber ? t("phoneNumberRequired") : t("invalidPhoneNumber"));
      setDisableSubmitButton(false); setLoading(false); return;
    }
    const formattedNumber = number.format("E.164");

    const jobApplicationId = doc(collection(db, `job-descriptions/${tenantID}/active/${jobUuid}/applications`));
    const baseFilename = `${tenantID}_${jobUuid}_${jobApplicationId.id}`;

    let uploadedResumeName = "";
    let uploadedResumeRef = "";
    let uploadedCoverLetterName = "";
    let uploadedCertificateName = "";
    let uploadedDiplomaName = "";
    let uploadedCoverLetterRef = "";
    let uploadedCertificateRef = "";
    let uploadedDiplomaRef = "";

    try {
      if (resume) {
        const fileUpload = await handleFileUpload(resume, "resumesactive", baseFilename);
        uploadedResumeName = fileUpload.uniqueFilename;
        uploadedResumeRef = fileUpload.storageRef;
      }
      if (coverLetter) {
        const fileUpload = await handleFileUpload(coverLetter, "coverletteractive", baseFilename);
        uploadedCoverLetterName = fileUpload.uniqueFilename;
        uploadedCoverLetterRef = fileUpload.storageRef;
      }
      if (certificatesDocument) {
        const fileUpload = await handleFileUpload(certificatesDocument, "certificationsactive", baseFilename);
        uploadedCertificateName = fileUpload.uniqueFilename;
        uploadedCertificateRef = fileUpload.storageRef;
      }
      if (diplomas) {
        const fileUpload = await handleFileUpload(diplomas, "diplomasactive", baseFilename);
        uploadedDiplomaName = fileUpload.uniqueFilename;
        uploadedDiplomaRef = fileUpload.storageRef;
      }
    } catch (uploadError) {
      setResumeUploadError(true);
      alert(t("uploadError"));
      setDisableSubmitButton(false); setLoading(false); return;
    }

    if (resumeUploadError || !uploadedResumeName) {
      alert(t("uploadError")); setDisableSubmitButton(false); setLoading(false); return;
    }

    try {
      // Remove local UI-only fields like 'id' from educations
      const cleanEducations = educations.map(({ id, ...rest }) => rest);

      const jobApplication = {
        ApplicantID: jobApplicationId.id, applicantId: jobApplicationId.id, type: jobDetails.type || "",
        FullName: fullName, Sex: sex, Score: 0, ScoreReason: "",
        ContactInformation: { Email: email, Phone: formattedNumber, Address: address, City: selectedCity, State: selectedState, Country: selectedCountry.name },
        PositionAppliedFor: jobDetails.Titre || "", companyName: jobDetails.companyName || "",
        ApplicationDate: Timestamp.now(), Diploma: uploadedDiplomaName || "", Resume: uploadedResumeName || "", resumeURL: uploadedResumeRef || "", originalResumePath: uploadedResumeRef, CoverLetter: uploadedCoverLetterName || "",
        Education: cleanEducations, qualifications: qualifications, Skills: skills, References: references, Portfolio: "",
        Certifications: certificates, CertificationDocument: uploadedCertificateName || "", CertificationDocumentURL: uploadedCertificateRef || "",
        Languages: languages, ApplicationStatus: "Submitted",
        tenantId: tenantID, jobUuid: jobUuid,
      };

      const jobApplicationRef = doc(db, `job-descriptions/${tenantID}/active/${jobUuid}/applications`, jobApplicationId.id);
      await setDoc(jobApplicationRef, jobApplication, { merge: true });

      logEvent(analytics, "application_submitted", { job_id: jobID, tenant_id: tenantID });
      await addDoc(collection(db, "send-grid-emails"), {
        template: true,
        templateId: "d-4ed996d0fa884f54806de429a08052eb",


        to: email,
        from: "customer@prorecruit.tech",
        fromName: jobDetails.companyName || "ProRecruit",
        templateData: {
          jobTitle: jobDetails.Titre || "",
          companyName: jobDetails.companyName || "",
          fullName: fullName || "",
          applicantName: fullName || "",
          applicantEmail: email || "",
          applicantPhone: formattedNumber || "",
          applicantAddress: address || "",
          applicantCity: selectedCity || "",
          applicantState: selectedState || "",
          applicantCountry: selectedCountry.name || "",
          applicationDate: Timestamp.now(),
          resume: uploadedResumeName || "",
          coverLetter: uploadedCoverLetterName || "",
          certificationDocument: uploadedCertificateName || "",
          diplomaDocument: uploadedDiplomaName || "",
          languages: languages || "",
          applicationStatus: "Submitted",
          tenantId: tenantID || "",
          tenantName: jobDetails.companyName || "ProRecruit",
          jobUuid: jobUuid || "",
          jobLanguage: jobLanguage || "fr",
        },
        timestamp: Timestamp.now(),
      });

      const taskPayload = { jobUUID: jobUuid, applicationUUID: jobApplicationId.id, tenantID: tenantID, language: jobLanguage };
      await fetch(`${process.env.REACT_APP_API_ENDPOINT}api/v1/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: `${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}resumeAnalysisv4`, payload: taskPayload }),
      });

      navigate("/thank-you", { state: { tenantId: tenantID, jobId: jobUuid, fullName: fullName, email: email, phoneNumber: phoneNumber, referrer: "application" }, replace: true });
    } catch (error) {
      console.error(error);
      if (process.env.REACT_APP_ENVIRONEMENT === "prod") Sentry.captureException(error);
      alert(t("applicationSubmissionError"));
    } finally {
      setDisableSubmitButton(false); setLoading(false);
    }
  };

  // --- RENDER STATES ---
  if (!jobID || jobNotFound) {
    return (
      <div className="min-h-screen bg-[#f4f7f6] flex items-center justify-center p-6 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl border border-slate-200/50 p-10 max-w-md w-full transition-all duration-300 text-center">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 font-bold tracking-tight mb-4">{!jobID ? t("searchForJob") : t("jobNotFound")}</h1>
          <p className="text-slate-500 mb-8">{!jobID ? t("searchJobText") : t("jobNotFoundText")}</p>
          <form onSubmit={handleJobIdSubmit} className="mb-6 space-y-4">
            <input type="text" value={jobIdInput} onChange={(e) => setJobIdInput(e.target.value)} required className={inputClasses} placeholder={t("jobIdPlaceholder")} />
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="group relative w-full flex justify-center py-4 px-8 border border-transparent text-sm font-semibold rounded-full text-white bg-[#A47713] hover:bg-[#8a6310] shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713]">
              {t("search")}
            </motion.button>
          </form>
          <button onClick={() => navigate("/job_listings")} className="text-sm font-semibold text-slate-500 hover:text-slate-900 font-bold tracking-tight transition">
            &larr; {t("backToListings")}
          </button>
        </motion.div>
      </div>
    );
  }

  if (!canApply) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl shadow-xl border border-slate-200/50 p-8 max-w-md w-full transition-all duration-300 text-center">
          <h2 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-4">{t("applicationClosedTitle")}</h2>
          <p className="text-slate-600 leading-relaxed mb-6">{t("applicationClosedText")}</p>
          <Link to="/job_listings" className="group relative inline-flex justify-center py-3 px-8 border border-transparent text-sm font-semibold rounded-full text-white bg-[#A47713] hover:bg-[#8a6310] shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713]">
            {t("backToOffers")}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-slate-50 text-slate-900 font-bold tracking-tight font-sans scroll-smooth overflow-hidden">
      {/* Background Parallax Texture */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0 opacity-40 pointer-events-none bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-transparent to-transparent" />
      <div className="absolute inset-0 z-0 opacity-[0.15] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiM2MzY2RjEiLz48L3N2Zz4=')] pointer-events-none" />

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-md">
          <ColorRing visible={true} height="120" width="120" colors={["#3B82F6", "#6366F1", "#8B5CF6", "#A855F7", "#D946EF"]} />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 flex flex-col lg:flex-row gap-12">

        {/* LEFT COLUMN: Job Details (Sticky) */}
        <motion.div
          initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
          className="lg:w-1/3"
        >
          <div className="sticky top-12 max-h-[85vh] overflow-y-auto bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-xl border border-blue-50/50 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
            <div className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-widest text-blue-600 bg-blue-100 rounded-full uppercase">
              {jobDetails.type || t("application")}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-bold tracking-tight mb-2 leading-tight">
              {jobDetails.Titre}
            </h1>
            <h3 className="text-xl font-medium text-slate-500 mb-8">{jobDetails.companyName}</h3>

            <div className="space-y-4">
              {jobDetails.location && (
                <div className="flex items-center text-slate-600 leading-relaxed">
                  <svg className="w-5 h-5 mr-3 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.242-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  <span><strong className="block text-sm">{t("location")}</strong> {jobDetails.location}</span>
                </div>
              )}
              <div className="flex items-center text-slate-600 leading-relaxed">
                <svg className="w-5 h-5 mr-3 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                <span><strong className="block text-sm">{t("deadline")}</strong> {jobDetails.DateLimiteDeCandidature || new Date().toLocaleDateString()}</span>
              </div>
            </div>

            <hr className="my-8 border-gray-200" />

            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              {jobDetails.companyDescription && (
                <div>
                  <h4 className="font-bold text-slate-900 font-bold tracking-tight mb-2 uppercase tracking-wide text-xs">{t("aboutCompany")}</h4>
                  <p className="whitespace-pre-line leading-relaxed">{jobDetails.companyDescription}</p>
                </div>
              )}

              {jobDetails.Description && (
                <div>
                  <h4 className="font-bold text-slate-900 font-bold tracking-tight mb-2 uppercase tracking-wide text-xs">{t("jobDescription")}</h4>
                  <p className="whitespace-pre-line leading-relaxed">{jobDetails.Description}</p>
                </div>
              )}

              {jobDetails.responsibilities && (
                <div>
                  <h4 className="font-bold text-slate-900 font-bold tracking-tight mb-2 uppercase tracking-wide text-xs">{t("responsibilities")}</h4>
                  <p className="whitespace-pre-line leading-relaxed">{jobDetails.responsibilities}</p>
                </div>
              )}

              {jobDetails.qualifications && (
                <div>
                  <h4 className="font-bold text-slate-900 font-bold tracking-tight mb-2 uppercase tracking-wide text-xs">{t("qualificationsSection")}</h4>
                  <p className="whitespace-pre-line leading-relaxed">{jobDetails.qualifications}</p>
                </div>
              )}

              {jobDetails.competences && (
                <div>
                  <h4 className="font-bold text-slate-900 font-bold tracking-tight mb-2 uppercase tracking-wide text-xs">{t("requiredSkills")}</h4>
                  <p className="whitespace-pre-line leading-relaxed">{jobDetails.competences}</p>
                </div>
              )}
            </div>

            <hr className="my-8 border-gray-200" />
            <p className="text-sm text-slate-500 leading-relaxed italic">
              {t("formInstruction")}
            </p>
          </div>
        </motion.div>

        {/* RIGHT COLUMN: The Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:w-2/3"
        >
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* 1. Personal Info Card */}
            <div className={cardClasses}>
              <h3 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-6 flex items-center">
                <span className="bg-blue-50 text-[#A47713] w-8 h-8 rounded-full inline-flex items-center justify-center text-sm mr-3 font-bold tracking-wide uppercase">1</span>
                {t("personalInformation")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClasses} htmlFor="fullName">{t("fullName")}</label>
                  <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="Jean Dupont" className={inputClasses} />
                </div>
                <div>
                  <label className={labelClasses}>{t("sex")}</label>
                  <select value={sex} onChange={(e) => setSex(e.target.value)} required className={inputClasses}>
                    <option value="">{t("select")}</option>
                    <option value="Male">{t("male")}</option>
                    <option value="Female">{t("female")}</option>
                    <option value="Other">{t("other")}</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClasses} htmlFor="email">{t("email")}</label>
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jean.dupont@email.com" className={inputClasses} />
                </div>
              </div>
            </div>

            {/* 2. Contact & Location Card */}
            <div className={cardClasses}>
              <h3 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-6 flex items-center">
                <span className="bg-blue-50 text-[#A47713] w-8 h-8 rounded-full inline-flex items-center justify-center text-sm mr-3 font-bold tracking-wide uppercase">2</span>
                {t("locationContact")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={labelClasses}>{t("country")}</label>
                  <select onChange={handleCountryChange} value={selectedCountryisoCode} required className={inputClasses}>
                    <option value="">{t("selectCountry")}</option>
                    {Country.getAllCountries().map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClasses}>{t("stateRegion")}</label>
                  <select value={selectedState} onChange={(e) => { setSelectedState(e.target.value); setSelectedCity(""); }} disabled={!selectedCountryisoCode} required className={inputClasses}>
                    <option value="">{t("selectStateRegion")}</option>
                    {State.getStatesOfCountry(selectedCountryisoCode).map(s => <option key={s.isoCode} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClasses}>{t("city")}</label>
                  <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedState} required className={inputClasses}>
                    <option value="">{t("selectCity")}</option>
                    {City.getCitiesOfCountry(selectedCountryisoCode).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClasses}>{t("phoneNumber")}</label>
                  <input type="tel" onChange={handlePhoneNumberChange} onBlur={handlePhoneNumberBlur} value={phoneNumber} required className={`${inputClasses} ${phoneNumberError ? 'border-red-400 focus:ring-red-400' : ''}`} placeholder="+33 6 12 34 56 78" />
                  {phoneNumberError && <p className="text-red-500 text-xs mt-1 font-medium">{phoneNumberError}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className={labelClasses} htmlFor="address">{t("address")}</label>
                  <input id="address" type="text" value={address} onChange={(e) => setAddress(e.target.value)} required placeholder="123 Rue de la République" className={inputClasses} />
                </div>
              </div>
            </div>

            {/* 3. Documents Card */}
            <div className={cardClasses}>
              <h3 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-6 flex items-center">
                <span className="bg-blue-50 text-[#A47713] w-8 h-8 rounded-full inline-flex items-center justify-center text-sm mr-3 font-bold tracking-wide uppercase">3</span>
                {t("documents")}
              </h3>
              <div className="space-y-6">
                <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 border-dashed">
                  <label className={labelClasses}>{t("uploadResume")} <span className="text-red-500">*</span></label>
                  <input type="file" name="resume" onChange={handleFileChange} required className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#A47713] hover:bg-[#8a6310] file:text-white hover:file:bg-blue-700 cursor-pointer transition-all" />
                  {resumeError && <p className="text-red-500 text-xs mt-2">{resumeError}</p>}
                </div>
                <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-200 border-dashed">
                  <label className={labelClasses}>{t("uploadCoverLetter")}</label>
                  <input type="file" name="coverLetter" onChange={handleFileChange} className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-200 file:text-slate-600 leading-relaxed hover:file:bg-gray-300 cursor-pointer transition-all" />
                  {coverLetterError && <p className="text-red-500 text-xs mt-2">{coverLetterError}</p>}
                </div>
                <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-200 border-dashed">
                  <label className={labelClasses}>{t("uploadDiploma")}</label>
                  <input type="file" name="diplomas" onChange={handleFileChange} className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-200 file:text-slate-600 leading-relaxed hover:file:bg-gray-300 cursor-pointer transition-all" />
                  {diplomaError && <p className="text-red-500 text-xs mt-2">{diplomaError}</p>}
                </div>
              </div>
            </div>

            {/* 4. Education Array Card */}
            <div className={cardClasses}>
              <h3 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-6 flex items-center">
                <span className="bg-blue-50 text-[#A47713] w-8 h-8 rounded-full inline-flex items-center justify-center text-sm mr-3 font-bold tracking-wide uppercase">4</span>
                {t("education")}
              </h3>
              <AnimatePresence>
                {educations.map((education, index) => (
                  <motion.div key={education.id || index} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-6 bg-slate-50 border border-slate-200 rounded-2xl relative group">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>{t("institution")}</label>
                        <input type="text" name="Institution" value={education.Institution} onChange={(e) => handleArrayInputChange(educations, setEducations, index, e)} required className={`${inputClasses} ${submissionAttempted && educationErrors[index]?.Institution ? "border-red-500" : ""}`} />
                      </div>
                      <div>
                        <label className={labelClasses}>{t("degree")}</label>
                        <input type="text" name="Degree" value={education.Degree} onChange={(e) => handleArrayInputChange(educations, setEducations, index, e)} required className={`${inputClasses} ${submissionAttempted && educationErrors[index]?.Degree ? "border-red-500" : ""}`} />
                      </div>
                      <div>
                        <label className={labelClasses}>{t("fieldOfStudy")}</label>
                        <input type="text" name="FieldOfStudy" value={education.FieldOfStudy} onChange={(e) => handleArrayInputChange(educations, setEducations, index, e)} required className={`${inputClasses} ${submissionAttempted && educationErrors[index]?.FieldOfStudy ? "border-red-500" : ""}`} />
                      </div>
                      <div>
                        <label className={labelClasses}>{t("graduationYear")}</label>
                        <input type="number" name="GraduationYear" value={education.GraduationYear} onChange={(e) => handleArrayInputChange(educations, setEducations, index, e)} required className={`${inputClasses} ${submissionAttempted && educationErrors[index]?.GraduationYear ? "border-red-500" : ""}`} />
                      </div>
                    </div>
                    {educations.length > 1 && (
                      <button type="button" onClick={() => addOrRemoveItem(educations, setEducations, null, index)} className="absolute top-4 right-4 text-red-400 hover:text-red-600 bg-red-50 p-2 rounded-full transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={() => addOrRemoveItem(educations, setEducations, { Institution: "", Degree: "", FieldOfStudy: "", GraduationYear: "" })} className="w-full py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors">
                + {t("addEducation")}
              </motion.button>
            </div>

            {/* 5. Qualifications & Skills */}
            <div className={cardClasses}>
              <h3 className="text-2xl font-bold text-slate-900 font-bold tracking-tight mb-6 flex items-center">
                <span className="bg-blue-50 text-[#A47713] w-8 h-8 rounded-full inline-flex items-center justify-center text-sm mr-3 font-bold tracking-wide uppercase">5</span>
                {t("skillsSection")}
              </h3>
              <div className="space-y-6">
                <div>
                  <label className={labelClasses}>{t("qualifications")}</label>
                  <textarea value={qualifications} onChange={(e) => setQualifications(e.target.value)} rows="4" className={inputClasses} placeholder={t("qualificationsPlaceholder")}></textarea>
                </div>
                <div>
                  <label className={labelClasses}>{t("skills")}</label>
                  <textarea value={skills} onChange={(e) => setSkills(e.target.value)} rows="4" className={inputClasses} placeholder={t("skillsPlaceholder")}></textarea>
                </div>
              </div>
            </div>

            {/* Submit Button Block */}
            <div className="pt-8 pb-12 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                type="submit"
                disabled={disableSubmitButton}
                className="group rounded-full bg-[#A47713] hover:bg-[#8a6310] px-8 py-4 text-sm font-semibold text-white shadow-xl transition-all hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {t("submitApplication")}
                <svg className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </motion.button>
            </div>

          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ApplicationsPage;